import type { LucideIcon } from "lucide-react";
import {
  Building2,
  CreditCard,
  FileText,
  Globe,
  Key,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Palette,
  PieChart,
  Receipt,
  Shield,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  UsersRound,
  Webhook,
  Workflow,
} from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { MenuCard } from "@/components/ui/menu-card";
import { useSession } from "@/lib/auth-client";
import { useTierInfo } from "@/lib/hooks/use-billing";
import { useT } from "@/lib/i18n/use-t";
import { hasPermission, type Permission } from "@/lib/permissions";
import { T } from "./page.i18n";

type SettingsCard = {
  href: string;
  titleKey: keyof typeof T.en_GB;
  descriptionKey: keyof typeof T.en_GB;
  icon: LucideIcon;
  highlight?: boolean;
};

export default function SettingsPage() {
  const t = useT(T);
  const { data: sessionData, isPending: sessionLoading } = useSession();
  const { data: tierInfo, isLoading: tierLoading } = useTierInfo();

  if (sessionLoading || tierLoading) {
    return null;
  }

  const user = sessionData?.user as { permissions?: string[] } | undefined;
  const userPermissions = (user?.permissions || ["advisor"]) as Permission[];

  // Check permissions
  const canAccessDesigner = hasPermission(userPermissions, "custom-fields");
  const canAccessDeveloper = hasPermission(userPermissions, "api-keys");
  const canAccessAdmin = hasPermission(userPermissions, "users");

  // TODO: Move to permissions
  const canAccessCustomInstruments = true;

  const showBilling = !!tierInfo?.tier && tierInfo.tier !== "enterprise";

  // Build cards list based on permissions
  const cards: SettingsCard[] = [];

  if (canAccessAdmin) {
    cards.push({
      href: "/settings/organization",
      titleKey: "organization",
      descriptionKey: "organizationDescription",
      icon: Building2,
    });
  }
  if (showBilling && canAccessAdmin) {
    cards.push({
      href: "/settings/billing",
      titleKey: "billing",
      descriptionKey: "billingDescription",
      icon: CreditCard,
      highlight: true,
    });
  }
  if (canAccessDesigner) {
    cards.push(
      {
        href: "/settings/clients",
        titleKey: "clients",
        descriptionKey: "clientsDescription",
        icon: Users,
      },
      {
        href: "/settings/dashboards",
        titleKey: "dashboards",
        descriptionKey: "dashboardsDescription",
        icon: LayoutDashboard,
      },
      {
        href: "/settings/flows",
        titleKey: "flows",
        descriptionKey: "flowsDescription",
        icon: Workflow,
      },
      {
        href: "/settings/risk-models",
        titleKey: "riskModels",
        descriptionKey: "riskModelsDescription",
        icon: Shield,
      },
      {
        href: "/settings/financial-situation-models",
        titleKey: "financialSituationModels",
        descriptionKey: "financialSituationModelsDescription",
        icon: LayoutGrid,
      },
      {
        href: "/settings/portfolios",
        titleKey: "portfolioCollections",
        descriptionKey: "portfolioCollectionsDescription",
        icon: PieChart,
      },
      {
        href: "/settings/combined-instruments",
        titleKey: "combinedInstruments",
        descriptionKey: "combinedInstrumentsDescription",
        icon: Layers,
      },
      {
        href: "/settings/pdf-templates",
        titleKey: "pdfTemplates",
        descriptionKey: "pdfTemplatesDescription",
        icon: FileText,
      },
      {
        href: "/settings/theme",
        titleKey: "theme",
        descriptionKey: "themeDescription",
        icon: Palette,
      },
      {
        href: "/settings/regional",
        titleKey: "regional",
        descriptionKey: "regionalDescription",
        icon: Globe,
      },
    );
  }
  if (canAccessDeveloper) {
    cards.push(
      {
        href: "/settings/api-keys",
        titleKey: "apiKeys",
        descriptionKey: "apiKeysDescription",
        icon: Key,
      },
      {
        href: "/settings/webhooks",
        titleKey: "webhooks",
        descriptionKey: "webhooksDescription",
        icon: Webhook,
      },
    );
  }
  if (canAccessAdmin) {
    cards.push(
      {
        href: "/settings/users",
        titleKey: "users",
        descriptionKey: "usersDescription",
        icon: Users,
      },
      {
        href: "/settings/teams",
        titleKey: "teams",
        descriptionKey: "teamsDescription",
        icon: UsersRound,
      },
      {
        href: "/settings/ai-settings",
        titleKey: "aiModels",
        descriptionKey: "aiModelsDescription",
        icon: Sparkles,
      },
      {
        href: "/settings/usage",
        titleKey: "usage",
        descriptionKey: "usageDescription",
        icon: Receipt,
      },
    );
  }
  if (canAccessAdmin || canAccessDeveloper) {
    cards.push({
      href: "/settings/sso",
      titleKey: "sso",
      descriptionKey: "ssoDescription",
      icon: ShieldCheck,
    });
  }
  if (canAccessDeveloper) {
    cards.push({
      href: "/settings/third-party",
      titleKey: "compliance",
      descriptionKey: "complianceDescription",
      icon: Shield,
    });
  }
  if (canAccessCustomInstruments) {
    cards.push({
      href: "/settings/custom-instruments",
      titleKey: "customInstruments",
      descriptionKey: "customInstrumentsDescription",
      icon: TrendingUp,
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t.settings} description={t.description} />

      <div className="grid gap-6 md:grid-cols-2">
        {cards.map((card) => (
          <MenuCard
            key={card.href}
            href={card.href}
            title={t[card.titleKey]}
            description={t[card.descriptionKey]}
            icon={card.icon}
            highlight={card.highlight}
          />
        ))}
      </div>
    </div>
  );
}
